import { useCallback, useEffect, useRef, useState } from "react";
import {
  Bot, Save, Send, RotateCcw, Plus, X, Check, MessageSquare,
  Phone, BookOpen, AlertTriangle, Store,
} from "lucide-react";
import { toast } from "sonner";
import { useAgencyWorkspace } from "@/context/AgencyWorkspaceContext";
import { useApiClient } from "@/lib/apiClient";
import { useAuth } from "@/context/AuthContext";

const TONES = [
  { key: "warm_professional", label: "Caldo e professionale" },
  { key: "informal", label: "Informale" },
  { key: "elegant", label: "Elegante" },
  { key: "direct", label: "Diretto" },
];
const LENGTHS = [
  { key: "short", label: "Breve" },
  { key: "medium", label: "Media" },
];
const EMOJI = [
  { key: "none", label: "Nessuna" },
  { key: "few", label: "Poche" },
];

const AUTO_FIELDS = [
  { key: "name", label: "Nome salone" },
  { key: "address", label: "Indirizzo" },
  { key: "opening_hours", label: "Orari" },
  { key: "services", label: "Servizi" },
  { key: "price_list", label: "Listino" },
];

export default function AgencyAiBotPage() {
  const { selectedTenant } = useAgencyWorkspace();
  const api = useApiClient();
  const { user } = useAuth();
  const canEdit = user?.role === "agency_admin";

  const [loading, setLoading] = useState(true);
  const [config, setConfig] = useState(null);
  const [knowledge, setKnowledge] = useState(null);
  const [status, setStatus] = useState(null);
  const [followup, setFollowup] = useState(null);
  const [ctxInfo, setCtxInfo] = useState(null);
  const [savingConfig, setSavingConfig] = useState(false);
  const [savingKnow, setSavingKnow] = useState(false);

  const loadSeq = useRef(0);
  const capabilityOff = selectedTenant && selectedTenant.capabilities
    && selectedTenant.capabilities.ai_lead_management === false;

  const load = useCallback(async () => {
    if (!selectedTenant) return;
    const seq = ++loadSeq.current;
    setLoading(true);
    setConfig(null); setKnowledge(null); setStatus(null); setCtxInfo(null);
    try {
      const [rc, rk, rx] = await Promise.all([
        api.get("/agency/ai-bot/config"),
        api.get("/agency/ai-bot/knowledge"),
        api.get("/agency/ai-bot/context").catch(() => ({ data: null })),
      ]);
      if (seq !== loadSeq.current) return;
      setConfig(rc.data.config);
      setStatus(rc.data.status);
      setFollowup(rc.data.followup_rules);
      setKnowledge(rk.data.knowledge);
      setCtxInfo(rx.data);
    } catch (e) {
      if (seq === loadSeq.current) {
        // capability off → gestita a parte; altri errori → toast
        if (!capabilityOff) toast.error(e?.response?.data?.detail || "Errore caricamento AI Bot");
      }
    } finally {
      if (seq === loadSeq.current) setLoading(false);
    }
  }, [api, selectedTenant, capabilityOff]);

  useEffect(() => { load(); }, [load]);

  const saveConfig = async (patch) => {
    setSavingConfig(true);
    try {
      const r = await api.patch("/agency/ai-bot/config", patch);
      setConfig(r.data.config);
      setStatus((s) => ({ ...s, ai_bot_enabled: !!r.data.config.enabled }));
      toast.success("Configurazione salvata");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore salvataggio");
    } finally { setSavingConfig(false); }
  };

  const saveKnowledge = async () => {
    setSavingKnow(true);
    try {
      const r = await api.patch("/agency/ai-bot/knowledge", {
        salon_positioning: knowledge.salon_positioning || "",
        work_method: knowledge.work_method || "",
        service_explanations: knowledge.service_explanations || "",
        practical_information: knowledge.practical_information || "",
        additional_ai_information: knowledge.additional_ai_information || "",
        terms_to_use: knowledge.terms_to_use || [],
        terms_to_avoid: knowledge.terms_to_avoid || [],
        things_not_to_promise: knowledge.things_not_to_promise || [],
        faq: (knowledge.faq || []).filter((f) => (f.question || "").trim() || (f.answer || "").trim()),
      });
      setKnowledge(r.data.knowledge);
      toast.success("Knowledge salvata");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore salvataggio knowledge");
    } finally { setSavingKnow(false); }
  };

  if (!selectedTenant) {
    return (
      <div data-testid="ai-bot-empty" className="sp-card p-10 text-center">
        <Store className="h-10 w-10 mx-auto text-[color:var(--sp-text-muted)] mb-3" />
        <h2 className="font-display text-xl font-black">Seleziona un salone</h2>
        <p className="text-sm text-[color:var(--sp-text-muted)] mt-2">Scegli un salone dal selettore in alto per configurare l'AI Bot.</p>
      </div>
    );
  }

  if (capabilityOff) {
    return (
      <div data-testid="ai-bot-capability-off" className="sp-card p-10 text-center">
        <Bot className="h-10 w-10 mx-auto text-[color:var(--sp-text-muted)] mb-3" />
        <h2 className="font-display text-xl font-black">AI Bot non attivo per questo salone</h2>
        <p className="text-sm text-[color:var(--sp-text-muted)] mt-2">
          La gestione lead AI non è abilitata per <b>{selectedTenant.name}</b>.
        </p>
      </div>
    );
  }

  return (
    <div data-testid="ai-bot-page" className="space-y-6">
      <header className="flex items-center gap-3">
        <div className="h-11 w-11 rounded-2xl bg-[color:var(--sp-brand)]/10 flex items-center justify-center flex-shrink-0">
          <Bot className="h-6 w-6 text-[color:var(--sp-brand)]" />
        </div>
        <div>
          <h1 className="font-display text-xl sm:text-2xl font-black tracking-tight" data-testid="ai-bot-title">
            AI Bot · {selectedTenant.name}
          </h1>
          <p className="text-xs text-[color:var(--sp-text-muted)]">
            Configura l'assistente WhatsApp del salone e provalo nel simulatore.
          </p>
        </div>
      </header>

      {loading || !config ? (
        <div className="text-sm text-[color:var(--sp-text-muted)]">Caricamento…</div>
      ) : (
        <div className="grid grid-cols-1 xl:grid-cols-[1fr_420px] gap-6 items-start">
          <div className="space-y-6 min-w-0">
            <StatoSection config={config} status={status} canEdit={canEdit}
              saving={savingConfig} onToggle={(v) => saveConfig({ enabled: v })} />
            <ComunicazioneSection config={config} canEdit={canEdit}
              onSave={saveConfig} saving={savingConfig} />
            <SchedaClienteSection knowledge={knowledge} setKnowledge={setKnowledge}
              canEdit={canEdit} onSave={saveKnowledge} saving={savingKnow} />
            <AutomazioniSection config={config} canEdit={canEdit}
              onSave={saveConfig} saving={savingConfig} followup={followup} />
            <DatiAutomaticiSection ctxInfo={ctxInfo} />
          </div>
          <SimulatorSection api={api} tenantId={selectedTenant.id} />
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
function Card({ title, icon: Icon, children, action }) {
  return (
    <section className="sp-card p-5">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-display text-lg font-black inline-flex items-center gap-2">
          {Icon && <Icon className="h-5 w-5 text-[color:var(--sp-brand)]" />} {title}
        </h3>
        {action}
      </div>
      {children}
    </section>
  );
}

function ReadOnlyBadge({ ok, label }) {
  return (
    <div className="flex items-center gap-2 text-sm">
      <span className={`h-2 w-2 rounded-full ${ok ? "bg-emerald-500" : "bg-slate-300"}`} />
      <span className="text-[color:var(--sp-text-secondary)]">{label}</span>
      <span className={`ml-auto text-xs font-bold ${ok ? "text-emerald-600" : "text-[color:var(--sp-text-muted)]"}`}>
        {ok ? "Sì" : "No"}
      </span>
    </div>
  );
}

// ---------------------------------------------------------------------------
function StatoSection({ config, status, canEdit, saving, onToggle }) {
  return (
    <Card title="Stato" icon={Bot}>
      <div className="flex items-center justify-between rounded-xl border border-[color:var(--sp-border)] p-4 bg-[color:var(--sp-subtle)]/40">
        <div>
          <div className="font-bold text-sm">AI Bot attivo per questo salone</div>
          <div className="text-xs text-[color:var(--sp-text-muted)] mt-0.5">
            Master switch. Se OFF, config e knowledge restano salvate ma il bot reale non agirà.
          </div>
        </div>
        <Toggle checked={!!config.enabled} disabled={!canEdit || saving}
          testid="ai-bot-master-toggle" onChange={onToggle} />
      </div>
      <div className="mt-4 space-y-2">
        <ReadOnlyBadge ok={!!status?.receptionist_assigned}
          label={status?.receptionist_assigned ? `Receptionist: ${status.receptionist_assigned}` : "Receptionist assegnata"} />
        <ReadOnlyBadge ok={!!status?.whatsapp_connected} label="WhatsApp collegato" />
        <ReadOnlyBadge ok={!!status?.knowledge_configured} label="Knowledge configurata" />
      </div>
    </Card>
  );
}

function ComunicazioneSection({ config, canEdit, onSave, saving }) {
  const [local, setLocal] = useState({
    tone: config.tone, response_length: config.response_length,
    emoji_usage: config.emoji_usage,
    communication_instructions: config.communication_instructions || "",
  });
  useEffect(() => {
    setLocal({
      tone: config.tone, response_length: config.response_length,
      emoji_usage: config.emoji_usage,
      communication_instructions: config.communication_instructions || "",
    });
  }, [config]);

  return (
    <Card title="Comunicazione" icon={MessageSquare} action={canEdit && (
      <button data-testid="ai-bot-save-comm" onClick={() => onSave(local)} disabled={saving}
        className="sp-btn-primary text-sm inline-flex items-center gap-2">
        <Save className="h-4 w-4" /> Salva
      </button>
    )}>
      <div className="space-y-4">
        <PillGroup label="Tono" options={TONES} value={local.tone} disabled={!canEdit}
          testid="ai-bot-tone" onChange={(v) => setLocal({ ...local, tone: v })} />
        <PillGroup label="Lunghezza risposta" options={LENGTHS} value={local.response_length} disabled={!canEdit}
          testid="ai-bot-length" onChange={(v) => setLocal({ ...local, response_length: v })} />
        <PillGroup label="Emoji" options={EMOJI} value={local.emoji_usage} disabled={!canEdit}
          testid="ai-bot-emoji" onChange={(v) => setLocal({ ...local, emoji_usage: v })} />
        <div>
          <label className="text-xs font-bold uppercase tracking-wider text-[color:var(--sp-text-muted)]">Indicazioni aggiuntive</label>
          <textarea rows={3} disabled={!canEdit} data-testid="ai-bot-instructions"
            value={local.communication_instructions}
            onChange={(e) => setLocal({ ...local, communication_instructions: e.target.value })}
            placeholder="Es. Dare del tu. Evitare tono aggressivamente commerciale. Non usare la parola sconto."
            className="sp-input mt-1 resize-none" />
        </div>
      </div>
    </Card>
  );
}

function SchedaClienteSection({ knowledge, setKnowledge, canEdit, onSave, saving }) {
  const set = (k, v) => setKnowledge((p) => ({ ...p, [k]: v }));
  return (
    <Card title="Scheda cliente" icon={BookOpen} action={canEdit && (
      <button data-testid="ai-bot-save-knowledge" onClick={onSave} disabled={saving}
        className="sp-btn-primary text-sm inline-flex items-center gap-2">
        <Save className="h-4 w-4" /> Salva
      </button>
    )}>
      <div className="space-y-4">
        <TextArea label="Presentazione / Posizionamento" value={knowledge.salon_positioning}
          disabled={!canEdit} testid="know-positioning" onChange={(v) => set("salon_positioning", v)} />
        <TextArea label="Metodo di lavoro" value={knowledge.work_method}
          disabled={!canEdit} testid="know-method" onChange={(v) => set("work_method", v)} />
        <TextArea label="Come descrivere i servizi" value={knowledge.service_explanations}
          disabled={!canEdit} testid="know-services" onChange={(v) => set("service_explanations", v)} />
        <TextArea label="Informazioni pratiche" value={knowledge.practical_information}
          disabled={!canEdit} testid="know-practical" onChange={(v) => set("practical_information", v)} />
        <FaqEditor faq={knowledge.faq || []} disabled={!canEdit}
          onChange={(v) => set("faq", v)} />
        <ChipEditor label="Termini da usare" items={knowledge.terms_to_use || []} disabled={!canEdit}
          testid="know-terms-use" onChange={(v) => set("terms_to_use", v)} />
        <ChipEditor label="Termini da evitare" items={knowledge.terms_to_avoid || []} disabled={!canEdit}
          testid="know-terms-avoid" onChange={(v) => set("terms_to_avoid", v)} />
        <ChipEditor label="Cose da non promettere" items={knowledge.things_not_to_promise || []} disabled={!canEdit}
          testid="know-no-promise" onChange={(v) => set("things_not_to_promise", v)} />
        <TextArea label="Altre informazioni utili per l'AI" value={knowledge.additional_ai_information}
          disabled={!canEdit} testid="know-additional" rows={4} onChange={(v) => set("additional_ai_information", v)} />
      </div>
    </Card>
  );
}

function AutomazioniSection({ config, canEdit, onSave, saving, followup }) {
  return (
    <Card title="Automazioni" icon={RotateCcw}>
      <div className="space-y-4">
        <div className="rounded-xl border border-[color:var(--sp-border)] p-4">
          <div className="flex items-center justify-between">
            <div className="font-bold text-sm">Follow-up automatici</div>
            <Toggle checked={!!config.automatic_followups_enabled} disabled={!canEdit || saving}
              testid="ai-bot-followups-toggle"
              onChange={(v) => onSave({ automatic_followups_enabled: v })} />
          </div>
          <div className="text-xs text-[color:var(--sp-text-muted)] mt-2 space-y-0.5">
            <div>1° follow-up: dopo {followup?.first_delay_hours || 6} ore di inattività</div>
            <div>2° follow-up: mattina successiva</div>
            <div>Finestra invio: {followup?.send_window_start || "09:00"}–{followup?.send_window_end || "20:00"}</div>
          </div>
        </div>
        <div className="rounded-xl border border-[color:var(--sp-border)] p-4">
          <div className="flex items-center justify-between">
            <div className="font-bold text-sm">Messaggio dopo chiamata non risposta</div>
            <Toggle checked={!!config.no_answer_message_enabled} disabled={!canEdit || saving}
              testid="ai-bot-noanswer-toggle"
              onChange={(v) => onSave({ no_answer_message_enabled: v })} />
          </div>
          <div className="text-xs text-[color:var(--sp-text-muted)] mt-2">
            Quando una chiamata non riceve risposta, l'AI potrà inviare un messaggio WhatsApp informando il lead che abbiamo provato a contattarlo.
          </div>
        </div>
      </div>
    </Card>
  );
}

function DatiAutomaticiSection({ ctxInfo }) {
  const presence = ctxInfo?.presence || {};
  const warnings = ctxInfo?.warnings || [];
  return (
    <Card title="Dati usati automaticamente" icon={Store}>
      <p className="text-xs text-[color:var(--sp-text-muted)] mb-3">
        Memento fornisce automaticamente all'AI i dati aggiornati del salone (mai duplicati qui).
      </p>
      <div className="grid grid-cols-2 gap-2">
        {AUTO_FIELDS.map((f) => (
          <div key={f.key} data-testid={`auto-data-${f.key}`}
            className="flex items-center gap-2 text-sm rounded-lg border border-[color:var(--sp-border)] px-3 py-2">
            {presence[f.key]
              ? <Check className="h-4 w-4 text-emerald-500 flex-shrink-0" />
              : <AlertTriangle className="h-4 w-4 text-amber-500 flex-shrink-0" />}
            <span className="text-[color:var(--sp-text-secondary)]">{f.label}</span>
          </div>
        ))}
      </div>
      {warnings.length > 0 && (
        <div className="mt-3 space-y-1" data-testid="auto-data-warnings">
          {warnings.map((w) => (
            <div key={w} className="text-xs text-amber-700 inline-flex items-center gap-1.5">
              <AlertTriangle className="h-3.5 w-3.5" /> {w}
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}

function SimulatorSection({ api, tenantId }) {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const listRef = useRef(null);

  // Reset ad ogni cambio tenant
  useEffect(() => { setMessages([]); setInput(""); }, [tenantId]);
  // Scroll SOLO il contenitore della chat, mai la pagina.
  useEffect(() => {
    const el = listRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages, busy]);

  const send = async () => {
    const text = input.trim();
    if (!text || busy) return;
    const next = [...messages, { role: "user", content: text }];
    setMessages(next); setInput(""); setBusy(true);
    try {
      const r = await api.post("/agency/ai-bot/simulate", { messages: next });
      const reply = r.data?.reply;
      if (r.data?.error || !reply) {
        toast.error(r.data?.error || "Nessuna risposta dall'AI");
      } else {
        setMessages((m) => [...m, { role: "assistant", content: reply }]);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore simulazione");
    } finally { setBusy(false); }
  };

  return (
    <section className="sp-card p-0 overflow-hidden flex flex-col xl:sticky xl:top-4"
      data-testid="ai-bot-simulator" style={{ height: "min(680px, calc(100vh - 96px))" }}>
      <div className="flex items-center justify-between px-4 py-3 border-b border-[color:var(--sp-border)] bg-[color:var(--sp-subtle)]/40 flex-shrink-0">
        <h3 className="font-display text-base font-black inline-flex items-center gap-2">
          <MessageSquare className="h-4 w-4 text-[color:var(--sp-brand)]" /> Test AI
        </h3>
        <button data-testid="ai-bot-sim-reset" onClick={() => { setMessages([]); setInput(""); }}
          className="sp-btn-ghost text-xs inline-flex items-center gap-1.5">
          <RotateCcw className="h-3.5 w-3.5" /> Nuova simulazione
        </button>
      </div>
      <div ref={listRef} className="flex-1 min-h-0 overflow-y-auto p-4 space-y-3" style={{ background: "#efeae2" }}>
        {messages.length === 0 && (
          <div className="text-center text-xs text-[color:var(--sp-text-muted)] mt-10">
            Scrivi un messaggio come farebbe un cliente per testare l'AI.
          </div>
        )}
        {messages.map((m, i) => (
          <div key={i} data-testid={`sim-msg-${m.role}-${i}`}
            className={`max-w-[85%] px-3 py-2 rounded-2xl text-sm whitespace-pre-wrap break-words ${
              m.role === "user"
                ? "ml-auto rounded-br-sm text-[#111b21]"
                : "mr-auto rounded-bl-sm bg-white text-[#111b21] shadow-sm"
            }`}
            style={m.role === "user" ? { background: "#d9fdd3" } : undefined}>
            {m.content}
          </div>
        ))}
        {busy && (
          <div className="mr-auto max-w-[85%] px-3 py-2 rounded-2xl rounded-bl-sm bg-white text-[#667781] text-sm shadow-sm">
            sta scrivendo…
          </div>
        )}
      </div>
      <div className="p-3 border-t border-[color:var(--sp-border)] flex items-end gap-2 flex-shrink-0 bg-white">
        <textarea rows={1} value={input} data-testid="ai-bot-sim-input"
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
          placeholder="Scrivi come cliente…"
          className="sp-input flex-1 resize-none text-sm py-2" />
        <button data-testid="ai-bot-sim-send" onClick={send} disabled={busy || !input.trim()}
          className="sp-btn-primary h-10 w-10 !p-0 flex items-center justify-center flex-shrink-0 disabled:opacity-40">
          <Send className="h-4 w-4" />
        </button>
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Sotto-componenti UI riusabili
// ---------------------------------------------------------------------------
function Toggle({ checked, disabled, onChange, testid }) {
  return (
    <button type="button" role="switch" aria-checked={checked} data-testid={testid}
      disabled={disabled}
      onClick={() => onChange(!checked)}
      className={`relative h-6 w-11 rounded-full transition-colors flex-shrink-0 ${
        checked ? "bg-[color:var(--sp-brand)]" : "bg-slate-300"
      } ${disabled ? "opacity-50 cursor-not-allowed" : ""}`}>
      <span className={`absolute top-1/2 -translate-y-1/2 h-5 w-5 rounded-full bg-white shadow transition-[left] duration-200 ${
        checked ? "left-[22px]" : "left-0.5"
      }`} />
    </button>
  );
}

function PillGroup({ label, options, value, disabled, onChange, testid }) {
  return (
    <div>
      <label className="text-xs font-bold uppercase tracking-wider text-[color:var(--sp-text-muted)]">{label}</label>
      <div className="mt-1.5 flex flex-wrap gap-1.5">
        {options.map((o) => (
          <button key={o.key} type="button" disabled={disabled}
            data-testid={`${testid}-${o.key}`}
            onClick={() => !disabled && onChange(o.key)}
            className={`px-3 py-1.5 rounded-full text-xs font-bold border transition-colors ${
              value === o.key
                ? "bg-[color:var(--sp-brand)] text-white border-[color:var(--sp-brand)]"
                : "bg-white border-[color:var(--sp-border)] text-[color:var(--sp-text-secondary)] hover:border-[color:var(--sp-brand)]/40"
            } ${disabled ? "opacity-60 cursor-not-allowed" : ""}`}>
            {o.label}
          </button>
        ))}
      </div>
    </div>
  );
}

function TextArea({ label, value, onChange, disabled, testid, rows = 3 }) {
  return (
    <div>
      <label className="text-xs font-bold uppercase tracking-wider text-[color:var(--sp-text-muted)]">{label}</label>
      <textarea rows={rows} disabled={disabled} data-testid={testid}
        value={value || ""} onChange={(e) => onChange(e.target.value)}
        className="sp-input mt-1 resize-none" />
    </div>
  );
}

function ChipEditor({ label, items, onChange, disabled, testid }) {
  const [draft, setDraft] = useState("");
  const add = () => {
    const v = draft.trim();
    if (!v) return;
    onChange([...(items || []), v]);
    setDraft("");
  };
  return (
    <div>
      <label className="text-xs font-bold uppercase tracking-wider text-[color:var(--sp-text-muted)]">{label}</label>
      <div className="mt-1.5 flex flex-wrap gap-1.5" data-testid={`${testid}-chips`}>
        {(items || []).map((it, i) => (
          <span key={i} className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-[color:var(--sp-subtle)] text-xs">
            {it}
            {!disabled && (
              <button type="button" data-testid={`${testid}-remove-${i}`}
                onClick={() => onChange(items.filter((_, j) => j !== i))}
                className="text-[color:var(--sp-text-muted)] hover:text-red-500">
                <X className="h-3 w-3" />
              </button>
            )}
          </span>
        ))}
        {(items || []).length === 0 && <span className="text-xs text-[color:var(--sp-text-muted)] italic">Nessuno</span>}
      </div>
      {!disabled && (
        <div className="mt-2 flex gap-2">
          <input value={draft} data-testid={`${testid}-input`}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); add(); } }}
            placeholder="Aggiungi e premi Invio" className="sp-input flex-1 !py-1.5 text-sm" />
          <button type="button" onClick={add} data-testid={`${testid}-add`}
            className="sp-btn-ghost text-sm inline-flex items-center gap-1">
            <Plus className="h-4 w-4" />
          </button>
        </div>
      )}
    </div>
  );
}

function FaqEditor({ faq, onChange, disabled }) {
  const update = (i, patch) => onChange(faq.map((f, j) => j === i ? { ...f, ...patch } : f));
  return (
    <div>
      <div className="flex items-center justify-between">
        <label className="text-xs font-bold uppercase tracking-wider text-[color:var(--sp-text-muted)]">FAQ</label>
        {!disabled && (
          <button type="button" data-testid="know-faq-add"
            onClick={() => onChange([...(faq || []), { question: "", answer: "" }])}
            className="text-xs font-bold text-[color:var(--sp-brand)] inline-flex items-center gap-1">
            <Plus className="h-3.5 w-3.5" /> Aggiungi FAQ
          </button>
        )}
      </div>
      <div className="mt-1.5 space-y-2" data-testid="know-faq-list">
        {(faq || []).length === 0 && <div className="text-xs text-[color:var(--sp-text-muted)] italic">Nessuna FAQ</div>}
        {(faq || []).map((f, i) => (
          <div key={i} data-testid={`know-faq-${i}`} className="rounded-lg border border-[color:var(--sp-border)] p-2.5 space-y-1.5">
            <div className="flex items-center gap-2">
              <input value={f.question || ""} disabled={disabled} data-testid={`know-faq-q-${i}`}
                onChange={(e) => update(i, { question: e.target.value })}
                placeholder="Domanda" className="sp-input flex-1 !py-1.5 text-sm font-bold" />
              {!disabled && (
                <button type="button" data-testid={`know-faq-remove-${i}`}
                  onClick={() => onChange(faq.filter((_, j) => j !== i))}
                  className="text-red-500 hover:bg-red-50 p-1.5 rounded"><X className="h-4 w-4" /></button>
              )}
            </div>
            <textarea rows={2} value={f.answer || ""} disabled={disabled} data-testid={`know-faq-a-${i}`}
              onChange={(e) => update(i, { answer: e.target.value })}
              placeholder="Risposta" className="sp-input resize-none text-sm" />
          </div>
        ))}
      </div>
    </div>
  );
}
